import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/firebaseInit';
import {
  activityRetentionDays,
  coll_activity,
  type ActivityDraftType,
  type ActivityType,
} from '@/types/types_activity';

// Data access for the partner activity log. Writing is open to partners (their
// own events only) and reading to admins — the asymmetry is the whole point of
// the collection, and is enforced by the security rules rather than here.

const activityCollection = collection(db, coll_activity);

function toActivity(id: string, data: Record<string, unknown>): ActivityType {
  return { ...(data as Omit<ActivityType, 'id'>), id };
}

/** Who an event belongs to. Taken from the signed-in user, never from a caller's argument. */
export interface ActivityIdentityType {
  uid: string;
  userEmail: string;
  partnerId: string;
}

/**
 * Records one event.
 *
 * Failures are logged and swallowed on purpose: this is called from the render
 * path of ordinary pages, and a partner losing its catalogue because a metric
 * could not be written would be a far worse outcome than a missing data point.
 */
export async function logActivity(
  identity: ActivityIdentityType,
  draft: ActivityDraftType,
): Promise<void> {
  try {
    await addDoc(activityCollection, {
      ...draft,
      ...identity,
      createdAt: new Date().toISOString(),
      // TTL marker — see `activityRetentionDays`. The rules insist it is far
      // enough out that setting it by hand cannot be used to make an event
      // disappear early.
      expireAt: new Date(Date.now() + activityRetentionDays * 24 * 60 * 60 * 1000),
    });
  } catch (e) {
    console.warn('Could not record activity', e);
  }
}

/**
 * The most events one read will return.
 *
 * There to bound "all time", which has no other end: nothing prunes this
 * collection today, so the query's range is however long the partner has had an
 * account. Newest first means the cap drops the oldest events rather than the
 * interesting ones, and the caller says so on screen when it bites.
 */
export const activityReadLimit = 5000;

/**
 * One partner's events, newest first, since `sinceIso` — or the lot, when no
 * start is given. Admins only: the rules refuse this to everyone else, partners
 * included.
 *
 * A one-shot read rather than a subscription: the caller is a chart over a
 * window of time, which nobody is watching tick over live, and a live listener
 * on a collection that grows with every page view is a standing cost for no
 * benefit.
 */
export async function listPartnerActivity(partnerId: string, sinceIso?: string): Promise<ActivityType[]> {
  const constraints: QueryConstraint[] = [where('partnerId', '==', partnerId)];
  if (sinceIso) constraints.push(where('createdAt', '>=', sinceIso));
  constraints.push(orderBy('createdAt', 'desc'), limit(activityReadLimit));

  const snap = await getDocs(query(activityCollection, ...constraints));
  return snap.docs.map((d) => toActivity(d.id, d.data()));
}
